import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';
import questionService from '../../services/questionService';

/**
 * Component for rating questions with 1-5 stars
 */
const QuestionRating = ({ 
  questionId, 
  isDarkMode, 
  themeStyles 
}) => {
  const [currentRating, setCurrentRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load current rating when question changes
  useEffect(() => {
    if (questionId) {
      loadCurrentRating();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  const loadCurrentRating = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const rating = await questionService.getQuestionRating(questionId);
      setCurrentRating(rating || 0);
    } catch (error) {
      // console.log('No existing rating found or error loading rating:', error);
      setCurrentRating(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStarClick = async (rating) => {
    try {
      setIsLoading(true);
      setError(null);
      await questionService.submitQuestionRating(questionId, rating);
      setCurrentRating(rating);
    } catch (error) {
      console.error('Error submitting rating:', error);
      setError('Failed to submit rating');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStarHover = (rating) => {
    if (!isLoading) {
      setHoverRating(rating);
    }
  };

  const handleMouseLeave = () => {
    setHoverRating(0);
  };

  const getStarStyle = (starIndex) => {
    const isActive = starIndex <= (hoverRating || currentRating);
    
    return {
      color: isActive 
        ? '#fbbf24' // Golden color for active stars
        : (isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'),
      fontSize: '14px', // Smaller stars for mobile
      cursor: isLoading ? 'default' : 'pointer',
      transition: 'color 0.2s ease',
      marginRight: '1px' // Reduced margin
    };
  };

  if (!questionId) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px', // Reduced gap for mobile
        padding: '4px 8px', // Reduced padding for mobile
        borderRadius: '6px',
        backgroundColor: isDarkMode 
          ? 'rgba(71, 85, 105, 0.4)' 
          : 'rgba(248, 250, 252, 0.8)',
        border: isDarkMode 
          ? '1px solid rgba(255, 255, 255, 0.1)' 
          : '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: isDarkMode 
          ? '0 2px 4px rgba(0, 0, 0, 0.15)' 
          : '0 1px 3px rgba(0, 0, 0, 0.05)',
        opacity: isLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
        flexShrink: 0, // Prevent shrinking
        minWidth: 'fit-content' // Ensure it doesn't get compressed
      }}
      onMouseLeave={handleMouseLeave}
    >
      <span
        style={{
          fontSize: '11px', // Smaller font for mobile
          fontWeight: 500,
          color: isDarkMode ? '#e2e8f0' : '#475569',
          marginRight: '2px', // Reduced margin
          whiteSpace: 'nowrap' // Prevent text wrapping
        }}
      >
        Rate:
      </span>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}> {/* Added gap between stars */}
        {[1, 2, 3, 4, 5].map((star) => (
          <FontAwesomeIcon
            key={star}
            icon={star <= (hoverRating || currentRating) ? faStar : faStarOutline}
            style={getStarStyle(star)}
            onClick={() => !isLoading && handleStarClick(star)}
            onMouseEnter={() => handleStarHover(star)}
          />
        ))}
      </div>

      {error && (
        <span
          style={{
            fontSize: '10px', // Smaller error text
            color: '#ef4444',
            marginLeft: '4px'
          }}
        >
          Error
        </span>
      )}
    </div>
  );
};

export default QuestionRating;
